import React, { useState, useEffect, useRef } from "react";
import CourseVideoCmp from "../course.video/course.video.cmp";
import CourseQuizCmp from "../course.quiz/course.quiz.cmp";
import "./course.player.cmp.scss";
import CourseModuleCmp from "../course.module/course.module.cmp";
import CourseOverviewCmp from "../course.overview/course.overview.cmp";
import CourseResourcesCmp from "../course.resources/course.resources.cmp";
import CourseTranscriptCmp from "../course.transcript/course.transcript.cmp";
import ModelCmp from "../../model/model.cmp";
import CourseSurveyCmp from "../course.survey/course.survey.cmp";
import CourseComander from "../../../store/commanders/course.comander";
import { useTranslation } from "react-i18next";
import CourseSponsors from "../course.sponsors/course.sponsors.cmp";

const CoursePlayer = ({
  selected,
  userName,
  state,
  isUserAdmin,
  onSelect,
  getTranscripts,
  showFeedbackForStudent,
  showCongrats,
  submitFeedback,
  userCertificates,
  course,
  courseTreeComponent,
}) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState("content");
  const [transcriptData, setTranscriptData] = useState(null);
  const [showFeedback, setShowFeedback] = useState(false);
  const [showCongratsModal, setShowCongratsModal] = useState(false);
  const surveyRef = useRef(null);
  const previousCourseStatus = useRef<string | null>(null);
  const previousCourseId = useRef<string | null>(null);

  const renderCongratsBody = () => (
    <div className="course-congrats">
      <div className="course-congrats__confetti" aria-hidden="true">
        {Array.from({ length: 24 }).map((_, index) => (
          <span
            key={index}
            className={`course-congrats__piece course-congrats__piece--${
              (index % 4) + 1
            }`}
          />
        ))}
      </div>
      <div className="course-congrats__emoji" aria-hidden="true">
        🎉
      </div>
      <div className="course-congrats__message">
        {t("course.congratulationsMessage")}
      </div>
      <div className="course-congrats__copy">
        {t("course.congratulationsCopy")}
      </div>
      <CourseSponsors />
    </div>
  );

  useEffect(() => {
    if (selected && selected.type && selected.type !== "video") {
      setActiveTab("content");
    }
  }, [selected?.type]);

  useEffect(() => {
    if (showCongrats) {
      setShowCongratsModal(true);
    }
  }, [showCongrats]);

  useEffect(() => {
    const currentCourseId = course?.id || null;
    const currentCourseStatus =
      course?.status || (course?.finish ? "pass" : null);

    if (!currentCourseId || !currentCourseStatus) {
      return;
    }

    if (
      previousCourseId.current !== currentCourseId ||
      previousCourseStatus.current === null
    ) {
      previousCourseId.current = currentCourseId;
      previousCourseStatus.current = currentCourseStatus;
      return;
    }

    if (
      previousCourseStatus.current !== "pass" &&
      currentCourseStatus === "pass"
    ) {
      setShowCongratsModal(true);
    }

    previousCourseId.current = currentCourseId;
    previousCourseStatus.current = currentCourseStatus;
  }, [course?.id, course?.finish, course?.status]);

  const closeCongratsModal = () => {
    setShowCongratsModal(false);

    if (showCongrats) {
      CourseComander.cmd_hide_congrats();
    }
  };

  const openFeedbackSurvey = () => {
    closeCongratsModal();
    setShowFeedback(true);
  };

  const goToDashboard = () => {
    setShowFeedback(false);

    if (course) {
      CourseComander.cmd_select_section(course);
    }
  };

  const setRenderer = (selected) => {
    if (!selected) return null;
    switch (selected.type) {
      case "video":
        return (
          <CourseVideoCmp
            selected={selected}
            getTranscripts={getTranscripts}
            onTranscriptData={setTranscriptData}
          />
        );
      case "quiz":
        return <CourseQuizCmp selected={selected} isUserAdmin={isUserAdmin} />;
      case "module":
        return <CourseModuleCmp selected={selected} />;
      case "resources":
        return <CourseResourcesCmp selected={selected} />;
      case "course":
        return (
          <CourseOverviewCmp
            selected={selected}
            state={state}
            userName={userName}
            onSelect={onSelect}
            showFeedbackForStudent={showFeedbackForStudent}
            showCongrats={showCongrats}
            submitFeedback={submitFeedback}
            userCertificates={userCertificates}
          />
        );
    }
  };

  return (
    <div className="h-100">
      {showCongratsModal ? (
        <ModelCmp
          name={t("course.congratulations")}
          bodyComponent={renderCongratsBody()}
          onCancel={closeCongratsModal}
          onSend={openFeedbackSurvey}
          sendButtonText={t("course.giveFeedback")}
          cancelButtonText={t("course.skip")}
          closeOnOutsideClick={false}
        />
      ) : null}

      {showFeedback && selected && selected.id ? (
        <ModelCmp
          name={t("course.feedback")}
          bodyComponent={
            <CourseSurveyCmp
              innerRef={surveyRef}
              courseId={selected.id}
              submitFeedback={submitFeedback}
              afterSubmitting={goToDashboard}
            />
          }
          onCancel={goToDashboard}
          onSend={() =>
            surveyRef.current.dispatchEvent(
              new Event("submit", {
                cancelable: true,
                bubbles: true,
              })
            )
          }
          sendButtonText={t("course.submitFeedback")}
          cancelButtonText={t("course.goToDashboard")}
        />
      ) : null}
      {/* IMPORTANT: .course-player-wrapper class is used by course tree for scroll-to-top - don't remove */}
      <div className="course-player-wrapper">
        {/* Video Content - Always rendered */}
        <div
          className={`video-content-area ${
            selected?.type === "video" ? "video-type" : ""
          } container`}
        >
          {setRenderer(selected)}
        </div>

        {/* Mobile Tabs - Hidden on desktop via CSS */}
        {
          <div className="mobile-tabs-container">
            <div
              className="mobile-tabs-header"
              role="tablist"
              aria-label={t("accessibility.courseNavigation")}
            >
              <button
                type="button"
                id="course-content-tab"
                role="tab"
                aria-selected={activeTab === "content"}
                aria-controls="course-content-panel"
                className={`mobile-tab ${
                  activeTab === "content" ? "active" : ""
                }`}
                onClick={() => setActiveTab("content")}
              >
                {t("course.content")}
              </button>

              {selected?.type === "video" && (
                <button
                  type="button"
                  id="course-transcript-tab"
                  role="tab"
                  aria-selected={activeTab === "transcripts"}
                  aria-controls="course-transcript-panel"
                  className={`mobile-tab ${
                    activeTab === "transcripts" ? "active" : ""
                  }`}
                  onClick={() => setActiveTab("transcripts")}
                >
                  {t("course.transcripts")}
                </button>
              )}
            </div>

            <div className="mobile-tab-content">
              {activeTab === "transcripts" && (
                <div
                  className="transcripts-tab"
                  id="course-transcript-panel"
                  role="tabpanel"
                  aria-labelledby="course-transcript-tab"
                  tabIndex={0}
                >
                  {transcriptData ? (
                    <CourseTranscriptCmp
                      transcripts={transcriptData.transcripts}
                      currentTime={transcriptData.currentTime}
                      loading={transcriptData.loading}
                      onWordClick={transcriptData.onWordClick}
                      className="mobile-transcript"
                    />
                  ) : (
                    <div role="status">{t("course.loadingTranscript")}</div>
                  )}
                </div>
              )}
              {activeTab === "content" && (
                <div
                  className="course-content-tab"
                  id="course-content-panel"
                  role="tabpanel"
                  aria-labelledby="course-content-tab"
                  tabIndex={0}
                >
                  {courseTreeComponent}
                </div>
              )}
            </div>
          </div>
        }
      </div>
    </div>
  );
};
export default CoursePlayer;
